import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../app_url';
import { Link } from 'react-router';
import './NotificationBell.scss';


import {
    useNotificationsUnreadCount
} from "@/queries/useNotificationsQuery"
import { useNotificationsList } from '../../queries/useNotificationsQuery';
import { useNotificationsListener } from '../../services/broadcast/broadcastService';

const NOTIFICATION_UNREAD_COUNT_STORAGE_KEY = 'doctolik_notifications_unread_count';
const NOTIFICATION_READ_IDS_STORAGE_KEY = 'doctolik_notifications_read_ids';

const getStoredUnreadCount = () => {
    const raw = localStorage.getItem(NOTIFICATION_UNREAD_COUNT_STORAGE_KEY);
    if (raw == null || raw === '') {
        return null;
    }

    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const getStoredReadNotificationIds = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(NOTIFICATION_READ_IDS_STORAGE_KEY) || '[]');
        return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
        return new Set();
    }
};

const storeReadNotificationIds = (ids) => {
    localStorage.setItem(NOTIFICATION_READ_IDS_STORAGE_KEY, JSON.stringify([...ids]));
};

const resolveUnreadCount = (serverCount) => {
    return Math.max(0, Number(serverCount ?? 0) || 0);
};

const NotificationBell = () => {
    const [notifications, setNotifications] = useState([])
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const listRef = useRef(null);

    const getHeader = () => {
        let value = JSON.parse(localStorage.getItem("doctolikLoginInfo"));
        return {
            headers: {
                Authorization: "Bearer " + value?.authorisation?.token,
            },
        };
    };

    const {
        data: NotificationsUnreadCount,
        isLoading: LoadingNotificationsUnreadCount,
        refetch: refetchNotificationsUnreadCount,
        isError: QueryError
    } = useNotificationsUnreadCount()

    const {
        data: NotificationsList,
        isLoading: LoadingNotificationsList,
        refetch: refetchNotificationsList,
        isSuccess: isSuccessNotificationsList
    } = useNotificationsList()

    useNotificationsListener({
        enabled: isSuccessNotificationsList,
        onEvent: () => {
            refetchNotificationsUnreadCount();
            refetchNotificationsList();
        }
    });

    const storedUnreadCount = getStoredUnreadCount();
    const storedReadNotificationIds = getStoredReadNotificationIds();
    const locallyPendingReadCount = (NotificationsList || []).filter((notification) => (
        storedReadNotificationIds.has(String(notification.id)) && !notification.is_read
    )).length;
    const unreadCount = Math.max(
        0,
        resolveUnreadCount(NotificationsUnreadCount?.count ?? 0) - locallyPendingReadCount
    );

    useEffect(() => {
        if (storedUnreadCount == null || unreadCount < storedUnreadCount) {
            localStorage.setItem(NOTIFICATION_UNREAD_COUNT_STORAGE_KEY, String(unreadCount));
        }
    }, [storedUnreadCount, unreadCount]);

    useEffect(() => {
        const nextReadIds = getStoredReadNotificationIds();
        const serverReadIds = new Set((NotificationsList || [])
            .filter((notification) => notification.is_read)
            .map((notification) => String(notification.id)));
        serverReadIds.forEach((id) => nextReadIds.delete(id));
        storeReadNotificationIds(nextReadIds);
        setNotifications((NotificationsList || []).map((notification) => ({
            ...notification,
            is_read: notification.is_read || nextReadIds.has(String(notification.id)),
        })));
    }, [NotificationsList])

    useEffect(() => {
        if (isOpen) {
            refetchNotificationsUnreadCount();
            refetchNotificationsList();
        }
    }, [isOpen, refetchNotificationsUnreadCount, refetchNotificationsList]);

    const markAsRead = async (id) => {
        const wasUnread = notifications.some(n => n.id === id && !n.is_read);

        if (!wasUnread) {
            return;
        }

        const priorUnreadCount = unreadCount;

        try {
            const readIds = getStoredReadNotificationIds();
            readIds.add(String(id));
            storeReadNotificationIds(readIds);
            setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: true } : n));
            const nextUnreadCount = Math.max(0, priorUnreadCount - 1);
            localStorage.setItem(NOTIFICATION_UNREAD_COUNT_STORAGE_KEY, String(nextUnreadCount));
            await axios.post(`${API_BASE_URL}v1/notifications/${id}/read`, {}, getHeader());
        } catch (error) {
            const readIds = getStoredReadNotificationIds();
            readIds.delete(String(id));
            storeReadNotificationIds(readIds);
            setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: false } : n));
            localStorage.setItem(NOTIFICATION_UNREAD_COUNT_STORAGE_KEY, String(priorUnreadCount));
            console.error("Error marking as read", error);
        }
    };



    useEffect(() => {
        if (isOpen) {
            // Scroll to top when opening dropdown
            setTimeout(() => {
                if (listRef.current) {
                    listRef.current.scrollTop = 0;
                }
            }, 0);
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getTypeBadgeClass = (type) => {
        const types = {
            'ALERTE': 'badge-danger',
            'INFORMATION': 'badge-info',
            'ANNONCE': 'badge-primary',
            'MISE_A_JOUR': 'badge-success',
            'ADMINISTRATION': 'badge-secondary',
        };
        return types[type] || 'badge-light';
    };

    return (
        <div className="notification-bell-container" ref={dropdownRef}>
            <button className="bell-button" onClick={() => setIsOpen(!isOpen)}>
                <Bell size={24} />
                {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
            </button>

            {isOpen && (
                <div className="notifications-dropdown">
                    <div className="dropdown-header">
                        <h3>Notifications</h3>
                    </div>
                    <div className="notifications-list" ref={listRef}>
                        {notifications.length === 0 ? (
                            <div className="no-notifications">Aucune notification</div>
                        ) : (
                            notifications.map((notif) => (
                                <div
                                    key={notif.id}
                                    className={`notification-item ${!notif.is_read ? 'unread' : ''}`}
                                    onClick={() => !notif.is_read && markAsRead(notif.id)}
                                >
                                    <div className="notif-header">
                                        <span className={`type-badge ${getTypeBadgeClass(notif.category)}`}>{notif.readable_type}</span>
                                        <span className="notif-date">{new Date(notif.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <div className="notif-title">{notif.title}</div>
                                    <div className="notif-content">{notif.content}</div>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="dropdown-footer">
                        <Link to="/doctor/notifications-history" onClick={() => setIsOpen(false)}>Voir tout l'historique</Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
